/**
 * Dynamic confirmation/review component that shows a summary of configured
 * connector fields grouped by field groups, with credential masking.
 *
 * This is the schema-driven counterpart to ConfirmConnector.tsx. Rather than
 * hard-coding field layouts per connector type, it reads the field definitions
 * and groups from the ConnectorTypeSchemaResponse and renders a read-only
 * summary using formatFieldValue for display formatting and credential masking.
 */
import type { ConnectorTypeSchemaResponse, ConnectorFieldSchema } from '../../../../types/connector-schema';
import { formatFieldValue } from '../../../../lib/validation/formatFieldValue';
import { isFieldVisible } from '../../../../lib/validation/fieldVisibility';

interface DynamicConfirmationProps {
  schema: ConnectorTypeSchemaResponse;
  values: Record<string, unknown>;
}

export function DynamicConfirmation({ schema, values }: DynamicConfirmationProps) {
  // Groups sorted by their declared order
  const groups = schema.field_groups
    .slice()
    .sort((a, b) => a.order - b.order);

  // Get visible fields for a given group, sorted by order
  const getGroupFields = (groupName: string): ConnectorFieldSchema[] => {
    return schema.fields
      .filter((f) => f.group === groupName && isFieldVisible(f, values))
      .sort((a, b) => a.order - b.order);
  };

  // Fields that don't belong to any group
  const ungroupedFields = schema.fields
    .filter((f) => !f.group && isFieldVisible(f, values))
    .sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6" role="list" aria-label="Configuration summary">
      {groups.map((group) => {
        const fields = getGroupFields(group.name);
        if (fields.length === 0) return null;

        return (
          <div key={group.name} role="listitem">
            <h4 className="text-sm font-medium text-foreground mb-3">
              {group.label}
            </h4>
            <div className="space-y-2 pl-4">
              {fields.map((field) => (
                <div key={field.name} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{field.label}</span>
                  <span className="font-medium text-foreground">
                    {formatFieldValue(field, values[field.name])}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {ungroupedFields.length > 0 && (
        <div role="listitem">
          <h4 className="text-sm font-medium text-foreground mb-3">
            Other Settings
          </h4>
          <div className="space-y-2 pl-4">
            {ungroupedFields.map((field) => (
              <div key={field.name} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{field.label}</span>
                <span className="font-medium text-foreground">
                  {formatFieldValue(field, values[field.name])}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
